import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Users.css';
import { getApiUrl, apiFetch } from '../../config/api';

const TaskTypesList = forwardRef(({ onSelectTaskType, selectedTaskTypeId, onNew }, ref) => {
  const { t } = useTranslation('admin');
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchTaskTypes
  }));

  useEffect(() => {
    fetchTaskTypes();
  }, [statusFilter, categoryFilter]);

  const fetchTaskTypes = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/task-types/');
      const params = [];
      if (statusFilter !== 'all') {
        params.push(`is_active=${statusFilter === 'active'}`);
      }
      if (categoryFilter !== 'all') {
        params.push(`category=${categoryFilter}`);
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      const response = await apiFetch(url);

      if (response.ok) {
        const data = await response.json();
        setTaskTypes(data);
      } else {
        setError(t('taskTypes.error.fetchFailed'));
      }
    } catch (err) {
      console.error('Error fetching task types:', err);
      setError(t('taskTypes.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredTaskTypes = taskTypes.filter(tt => {
    const matchesSearch = (
      tt.task_type_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tt.task_type_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tt.description && tt.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    return matchesSearch;
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('taskTypes.filters.status.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('taskTypes.filters.status.inactive')}</span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      PRODUCTION: '#28a745',
      WORKFLOW: '#007bff',
      MAINTENANCE: '#fd7e14',
      OTHER: '#6c757d'
    };
    return (
      <span
        className="status-badge"
        style={{ backgroundColor: categoryColors[category] || '#6c757d', color: '#fff' }}
      >
        {category}
      </span>
    );
  };

  if (loading && taskTypes.length === 0) {
    return <div className="list-container"><div className="loading">{t('taskTypes.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('taskTypes.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={onNew}>
            <i className="icon">+</i> {t('taskTypes.newTaskType')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('taskTypes.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('taskTypes.filters.allStatus')}</option>
            <option value="active">{t('taskTypes.filters.status.active')}</option>
            <option value="inactive">{t('taskTypes.filters.status.inactive')}</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('taskTypes.filters.allCategories')}</option>
            <option value="PRODUCTION">{t('taskTypes.filters.categories.production')}</option>
            <option value="WORKFLOW">{t('taskTypes.filters.categories.workflow')}</option>
            <option value="MAINTENANCE">{t('taskTypes.filters.categories.maintenance')}</option>
            <option value="OTHER">{t('taskTypes.filters.categories.other')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('taskTypes.tableHeaders.code')}</th>
              <th>{t('taskTypes.tableHeaders.name')}</th>
              <th>{t('taskTypes.tableHeaders.category')}</th>
              <th>{t('taskTypes.tableHeaders.configTable')}</th>
              <th>{t('taskTypes.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredTaskTypes.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? t('taskTypes.noData.searching') : t('taskTypes.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredTaskTypes.map(tt => (
                <tr
                  key={tt.task_type_id}
                  onClick={() => onSelectTaskType(tt.task_type_id)}
                  className={selectedTaskTypeId === tt.task_type_id ? 'selected' : ''}
                >
                  <td>
                    <span className="dept-code">
                      {tt.icon && <span style={{ marginRight: '4px' }}>{tt.icon}</span>}
                      {tt.task_type_code}
                    </span>
                  </td>
                  <td>
                    <strong>{tt.task_type_name}</strong>
                  </td>
                  <td>
                    {getCategoryBadge(tt.category)}
                  </td>
                  <td>
                    <span className="dept-description">{tt.config_table_name || '-'}</span>
                  </td>
                  <td>
                    {getStatusBadge(tt.is_active)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default TaskTypesList;
